import { observable, runInAction } from 'mobx';
import API from '../util/ApiUtil';
import GaHelper from '../util/GaHelper';
import RouteStore from './RouteStore';
import RootStore from './RootStore';
import LanguageStore, { Language } from './LanguageStore';
import { IFooterItem } from 'telia-front-react';
import { MenuItem } from '@teliaee/diil-selfservice-components';
import ErrorUtil from '../util/ErrorUtil';
import React from 'react';

export interface Post {
  postId: number;
  label: string;
}

export interface Menu {
  label?: string;
  items?: MenuItem[];
  postsByPath?: { [key: string]: Post };
  status?: Status;
}

class MenuStore {
  public initialState: InitialState;
  @observable accessor headerMenu: Menu;
  @observable accessor footerMenu: Menu = { status: 'UNINITIALIZED' };
  @observable private accessor selectedLanguage: Language;

  @observable private accessor allPostsByPath: { [key: string]: Post } = {};
  private routeStore: RouteStore;
  private languageStore: LanguageStore;
  private app: App;

  public constructor(rootStore: RootStore) {
    this.routeStore = rootStore.routeStore;
    this.languageStore = rootStore.languageStore;
    this.app = rootStore.app;
    this.init(rootStore.initialState);
  }

  private init({ footerMenu, headerMenu }: { footerMenu: Menu; headerMenu: Menu }) {
    const { currentLanguage: lang, initialLanguageChanged } = this.languageStore;
    if (!initialLanguageChanged && headerMenu) {
      runInAction(() => (this.headerMenu = headerMenu));
    }
    if (!initialLanguageChanged && footerMenu) {
      this.footerMenu = { status: 'FETCHED', ...footerMenu };
      this.updatePostsByPath();
    } else {
      this.fetchMenusFromServer(lang);
    }
  }

  public fetchMenusFromServer(lang: string) {
    if (this.footerMenu.status === 'FETCHING') {
      return;
    }
    this.fetchFooterMenu(lang).then(() => this.updatePostsByPath());
  }

  public fetchFooterMenu(lang: string = 'et'): Promise<Menu | void> {
    runInAction(() => (this.footerMenu.status = 'FETCHING'));
    return API.get(`/menu/footer/${lang}`, { params: { app: this.app } })
      .then((response) => {
        runInAction(() => {
          this.footerMenu = response.data;
          this.footerMenu.status = 'FETCHED';
        });
      })
      .catch((e) => {
        ErrorUtil.pushErrorToTeliaRumAndLog(e);
        runInAction(() => (this.footerMenu.status = 'ERROR'));
      });
  }

  private updatePostsByPath() {
    if (this.headerMenu && this.headerMenu.postsByPath && this.footerMenu && this.footerMenu.postsByPath) {
      runInAction(() => (this.allPostsByPath = { ...this.headerMenu.postsByPath, ...this.footerMenu.postsByPath }));
    }
  }

  public updateHeaderState() {
    if (this.selectedLanguage !== this.languageStore.currentLanguage) {
      this.fetchMenusFromServer(this.languageStore.currentLanguage);
      runInAction(() => (this.selectedLanguage = this.languageStore.currentLanguage));
    }
  }

  public postByPath(path: string) {
    const postsByPath: { [key: string]: Post } = this.allPostsByPath;
    return postsByPath ? postsByPath[path] : null;
  }

  public get menuPostsByPath(): Map<string, Post> {
    return new Map(Object.entries(this.allPostsByPath));
  }

  public footerNav() {
    return this.createFooterNavTree(this.footerMenu.items);
  }

  private createFooterNavTree(items: MenuItem[]): IFooterItem[] {
    return items ? items.map((item: MenuItem) => this.mapFooterItemForNavTree(item)) : [];
  }

  private mapFooterItemForNavTree(item: MenuItem): IFooterItem {
    const url = this.languageStore.pathForCurrentLang(item.path);
    return {
      label: item.label,
      url,
      onClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
        if (event.target?.href.startsWith('javascript:')) {
          return;
        }
        GaHelper.pushGaData({ eventAction: 'Footer Item', eventLabel: item.label });
        this.routeStore.navigate(event);
      }
    };
  }
}

export default MenuStore;
